import React, { useState } from 'react';
import WalterLoader from '../shared/WalterLoader';
import { useUsageTaxonomy } from '../shared/useUsageTaxonomy';
import { slugify } from '../../utils/slugify';
import '../../styles/Admin.css';

const API_URL = process.env.REACT_APP_API_URL;

const postJson = (path, body) => fetch(`${API_URL}${path}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
});

const putJson = (path, body) => fetch(`${API_URL}${path}`, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
});

const nowIso = () => new Date().toISOString();

const ActiveToggle = ({ active, onChange, label }) => (
    <div className="admin-active-toggle">
        <span className="admin-active-toggle-label">{label}</span>
        <div
            className={`toggle-switch${active ? ' active' : ''}`}
            role="switch" aria-checked={active} tabIndex={0}
            onClick={onChange}
            onKeyDown={(e) => e.key === ' ' && onChange()}
        >
            <div className="toggle-slider" />
        </div>
    </div>
);

/* ─────────────────────────────────────────────
   Écran d'admin pour la taxonomie d'usages (catégories / sous-catégories).
   V1 volontairement simple : cartes imbriquées + petits formulaires inline —
   pas de suppression dure (juste active/inactive), la nomenclature est
   amenée à beaucoup bouger tant qu'elle n'est pas stabilisée par le métier.
   L'ordre reste éditable via un champ numérique (pas de drag) : c'est
   ponctuel, réservé à un besoin d'affichage précis.
───────────────────────────────────────────── */
const UsageTaxonomy = () => {
    const { taxonomy, loading, reload } = useUsageTaxonomy();
    const [newCategoryLabel, setNewCategoryLabel] = useState('');
    const [newSubcategory, setNewSubcategory] = useState({}); // { [categoryId]: label }

    if (loading) {
        return (
            <div className="centered-spinner-dashboard">
                <WalterLoader />
            </div>
        );
    }

    const categories = taxonomy
        .filter((n) => n.type === 'category')
        .sort((a, b) => a.order - b.order);
    const subcategoriesOf = (categoryId) => taxonomy
        .filter((n) => n.type === 'subcategory' && n.parent_id === categoryId)
        .sort((a, b) => a.order - b.order);

    const createCategory = async () => {
        const label = newCategoryLabel.trim();
        if (!label) return;
        const _id = slugify(label);
        const order = categories.length ? Math.max(...categories.map((c) => c.order)) + 1 : 0;
        await postJson('/load_new_usage_category', {
            _id, type: 'category', parent_id: null, label, order, active: true,
            created_at: nowIso(), updated_at: nowIso(),
        });
        setNewCategoryLabel('');
        reload();
    };

    const createSubcategory = async (categoryId) => {
        const label = (newSubcategory[categoryId] || '').trim();
        if (!label) return;
        const siblings = subcategoriesOf(categoryId);
        const _id = `${categoryId}--${slugify(label)}`;
        const order = siblings.length ? Math.max(...siblings.map((s) => s.order)) + 1 : 0;
        await postJson('/load_new_usage_subcategory', {
            _id, type: 'subcategory', parent_id: categoryId, label, order, active: true,
            aliases: [], created_at: nowIso(), updated_at: nowIso(),
        });
        setNewSubcategory((prev) => ({ ...prev, [categoryId]: '' }));
        reload();
    };

    const renameCategory = async (categoryId, label) => {
        if (!label.trim()) return;
        await putJson(`/update_usage_category/${categoryId}`, { label: label.trim(), updated_at: nowIso() });
        reload();
    };

    const renameSubcategory = async (subcategoryId, label) => {
        if (!label.trim()) return;
        // update_usage_subcategory resynchronise aussi type_usage sur les usages
        // déjà catégorisés avec cette sous-catégorie (voir DB_processor.py).
        await putJson(`/update_usage_subcategory/${subcategoryId}`, { label: label.trim(), updated_at: nowIso() });
        reload();
    };

    const toggleCategoryActive = async (category) => {
        await putJson(`/update_usage_category/${category._id}`, { active: !category.active, updated_at: nowIso() });
        reload();
    };

    const toggleSubcategoryActive = async (subcategory) => {
        await putJson(`/update_usage_subcategory/${subcategory._id}`, { active: !subcategory.active, updated_at: nowIso() });
        reload();
    };

    const reorderCategory = async (category, order) => {
        if (Number.isNaN(order)) return;
        await putJson(`/update_usage_category/${category._id}`, { order, updated_at: nowIso() });
        reload();
    };

    const reorderSubcategory = async (subcategory, order) => {
        if (Number.isNaN(order)) return;
        await putJson(`/update_usage_subcategory/${subcategory._id}`, { order, updated_at: nowIso() });
        reload();
    };

    const reparentSubcategory = async (subcategory, newParentId) => {
        await putJson(`/update_usage_subcategory/${subcategory._id}`, { parent_id: newParentId, updated_at: nowIso() });
        reload();
    };

    return (
        <div className="admin-page-content admin-page-content--narrow-top">
            <div className="admin-taxonomy-list">
                {categories.map((category) => (
                    <div key={category._id} className={`admin-category-card${category.active ? '' : ' inactive'}`}>
                        <div className="admin-category-header">
                            <input
                                type="text" className="admin-row-input" defaultValue={category.label}
                                onBlur={(e) => e.target.value !== category.label && renameCategory(category._id, e.target.value)}
                            />
                            <span className="admin-slug-tag">{category._id}</span>
                            <span className="admin-order-label">ordre</span>
                            <input
                                type="number" className="admin-order-input" defaultValue={category.order}
                                onBlur={(e) => reorderCategory(category, parseInt(e.target.value, 10))}
                            />
                            <ActiveToggle active={category.active} label="active"
                                onChange={() => toggleCategoryActive(category)} />
                        </div>

                        <div className="admin-subcategory-list">
                            {subcategoriesOf(category._id).map((subcategory) => (
                                <div key={subcategory._id} className={`admin-subcategory-row${subcategory.active ? '' : ' inactive'}`}>
                                    <input
                                        type="text" className="admin-row-input" defaultValue={subcategory.label}
                                        onBlur={(e) => e.target.value !== subcategory.label && renameSubcategory(subcategory._id, e.target.value)}
                                    />
                                    <select
                                        className="admin-subcategory-parent-select"
                                        value={category._id}
                                        onChange={(e) => reparentSubcategory(subcategory, e.target.value)}
                                    >
                                        {categories.map((c) => <option key={c._id} value={c._id}>{c.label}</option>)}
                                    </select>
                                    <span className="admin-order-label">ordre</span>
                                    <input
                                        type="number" className="admin-order-input" defaultValue={subcategory.order}
                                        onBlur={(e) => reorderSubcategory(subcategory, parseInt(e.target.value, 10))}
                                    />
                                    <span className="admin-slug-tag">{subcategory._id}</span>
                                    <ActiveToggle active={subcategory.active} label="active"
                                        onChange={() => toggleSubcategoryActive(subcategory)} />
                                </div>
                            ))}

                            <div className="admin-add-row">
                                <input
                                    type="text" placeholder="Nouvelle sous-catégorie"
                                    value={newSubcategory[category._id] || ''}
                                    onChange={(e) => setNewSubcategory((prev) => ({ ...prev, [category._id]: e.target.value }))}
                                    onKeyDown={(e) => e.key === 'Enter' && createSubcategory(category._id)}
                                />
                                <button type="button" className="admin-add-btn"
                                    onClick={() => createSubcategory(category._id)}
                                    disabled={!(newSubcategory[category._id] || '').trim()}>
                                    + Ajouter
                                </button>
                            </div>
                        </div>
                    </div>
                ))}
            </div>

            <div className="admin-card">
                <div className="admin-add-row" style={{ borderTop: 'none' }}>
                    <input
                        type="text" placeholder="Nouvelle catégorie"
                        value={newCategoryLabel}
                        onChange={(e) => setNewCategoryLabel(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && createCategory()}
                    />
                    <button type="button" className="admin-add-btn" onClick={createCategory} disabled={!newCategoryLabel.trim()}>
                        + Nouvelle catégorie
                    </button>
                </div>
            </div>
        </div>
    );
};

export default UsageTaxonomy;
